import React, { useEffect, useState, type ComponentPropsWithoutRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Calendar,
    Clock,
    User,
    List,
    HelpCircle,
    BookOpen,
    Tag,
    Linkedin,
    Facebook,
    Link2,
    Check,
    Share2,
    X,
} from 'lucide-react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { trackBlogArticleShare } from '../../analytics';
import { useBlogArticleAnalytics } from './useBlogArticleAnalytics';
import { buildApiUrl } from '../../config/apiConfig';
import {
  blogArticlePath,
  blogBasePath,
  blogCategoryPath,
  type MarketingLocale,
} from '../../config/localeRoutes';
import ReactMarkdown, { type ExtraProps } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SEOHead from '../../components/SEO/SEOHead';
import { buildBlogArticleAlternatePaths } from '../../utils/blogArticleHreflang';
import MarketingCTA from '../../components/Marketing/MarketingCTA';
import { buildArticleSchema, buildBreadcrumbSchema, buildFAQPageSchema, absoluteCanonicalUrl, clipMetaDescription } from '../../components/SEO/schemas';
import { resolveBlogArticleFaqItems } from '../../utils/extractBlogFaqFromMarkdown';
import { reactNodeToPlainText, slugifyBlogHeadingSource } from '../../utils/blogHeadingSlug';
import {
  buildSocialShareUrl,
  canUseNativeShare,
  copyTextToClipboard,
  openSharePopup,
  shareNative,
  type SocialSharePlatform,
} from './articleShare';
import './BlogArticlePage.css';

const PRO_SITE_URL = 'https://www.rintintin-pro.com';
const SOCIAL_PLATFORMS = new Set<SocialSharePlatform>(['twitter', 'linkedin', 'facebook']);

interface Category {
    id: string;
    name: string;
    slug: string;
    icon: string;
    color: string;
}

interface TOCItem {
    level: number;
    text: string;
    slug: string;
}

interface FAQItem {
    question: string;
    answer: string;
}

interface RelatedArticle {
    id: string;
    title: string;
    slug: string;
    excerpt: string;
    featured_image_url: string;
    reading_time_minutes: number;
    published_at: string;
}

type MarkdownHeadingProps<T extends 'h1' | 'h2' | 'h3'> = ComponentPropsWithoutRef<T> & ExtraProps;

interface Article {
    id: string;
    title: string;
    slug: string;
    excerpt: string;
    content: string;
    content_html?: string;
    featured_image_url: string;
    featured_image_alt: string;
    reading_time_minutes: number;
    published_at: string;
    updated_at: string;
    tags: string[];
    category: Category | null;
    author_name: string;
    author_avatar_url?: string;
    meta_title: string;
    meta_description: string;
    table_of_contents: TOCItem[];
    faq_items: FAQItem[];
    schema_data: Record<string, unknown>;
    related_articles: RelatedArticle[];
    view_count: number;
    language?: string;
    translations?: {
        id: string;
        language: string;
        slug: string;
        title: string;
        market?: string | null;
    }[];
    market?: string | null;
}

interface ArticleShareButtonsProps {
    onShare: (platform: string) => void;
    onCopy: () => void;
    copied: boolean;
    copyFailed?: boolean;
    canNativeShare?: boolean;
}

function ArticleShareButtons({
    onShare,
    onCopy,
    copied,
    copyFailed = false,
    canNativeShare = false,
}: ArticleShareButtonsProps) {
    const { t } = useTranslation('marketing');

    return (
        <div className="share-buttons">
            {canNativeShare ? (
                <button
                    type="button"
                    onClick={() => onShare('native')}
                    className="share-btn share-native"
                    aria-label={t('blogArticlePage.share.native')}
                >
                    <Share2 size={18} strokeWidth={2.25} aria-hidden />
                </button>
            ) : null}
            <button
                type="button"
                onClick={() => onShare('twitter')}
                className="share-btn share-twitter"
                aria-label={t('blogArticlePage.share.onX')}
            >
                <span aria-hidden="true">X</span>
            </button>
            <button
                type="button"
                onClick={() => onShare('linkedin')}
                className="share-btn share-linkedin"
                aria-label={t('blogArticlePage.share.onLinkedIn')}
            >
                <Linkedin size={18} strokeWidth={2.25} aria-hidden />
            </button>
            <button
                type="button"
                onClick={() => onShare('facebook')}
                className="share-btn share-facebook"
                aria-label={t('blogArticlePage.share.onFacebook')}
            >
                <Facebook size={18} strokeWidth={2.25} aria-hidden />
            </button>
            <button
                type="button"
                onClick={onCopy}
                className={`share-btn share-copy${copyFailed ? ' share-btn--error' : ''}`}
                aria-label={
                    copied
                        ? t('blogArticlePage.share.linkCopied')
                        : copyFailed
                          ? t('blogArticlePage.share.copyFailed')
                          : t('blogArticlePage.share.copyLink')
                }
            >
                {copied ? (
                    <Check size={18} strokeWidth={2.25} aria-hidden />
                ) : copyFailed ? (
                    <X size={18} strokeWidth={2.25} aria-hidden />
                ) : (
                    <Link2 size={18} strokeWidth={2.25} aria-hidden />
                )}
            </button>
        </div>
    );
}

type BlogArticlePageProps = {
    locale?: MarketingLocale;
};

const BlogArticlePage: React.FC<BlogArticlePageProps> = ({ locale = 'fr' }) => {
    const { slug } = useParams<{ slug: string }>();
    const { t, i18n } = useTranslation('marketing');

    const [article, setArticle] = useState<Article | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
    const [linkCopied, setLinkCopied] = useState(false);
    const [copyFailed, setCopyFailed] = useState(false);
    const canNativeShare = React.useMemo(() => canUseNativeShare(), []);
    const { trackCta, trackRelated } = useBlogArticleAnalytics({
        slug: article?.slug || slug,
        categorySlug: article?.category?.slug,
        locale,
        enabled: Boolean(article && !loading && !error),
    });

    useEffect(() => {
        if (slug) {
            fetchArticle();
        }
    }, [slug, locale]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        (window as Window & { prerenderReady?: boolean }).prerenderReady = !loading;
    }, [loading, article, error]);

    const buildSeoSchemas = () => {
        if (!article) return [];
        const schemas: Record<string, unknown>[] = [];

        schemas.push(
            buildArticleSchema({
                title: article.title,
                description: clipMetaDescription(article.meta_description || article.excerpt || ''),
                image: article.featured_image_url,
                datePublished: article.published_at,
                dateModified: article.updated_at,
                authorName: article.author_name || 'Rintintin Pro',
                url: absoluteCanonicalUrl(blogArticlePath(locale, article.slug)),
                inLanguage: locale,
                articleSection: article.category?.name,
                isPartOfUrl: absoluteCanonicalUrl(blogBasePath(locale)),
                isPartOfName: t('blogPage.seo.schemaName'),
                readingTimeMinutes: article.reading_time_minutes,
                content: article.content,
            })
        );

        const breadcrumbItems = [
            { name: t('blogPage.breadcrumb.home'), path: locale === 'fr' ? '/' : `/${locale}` },
            { name: t('blogPage.breadcrumb.blog'), path: blogBasePath(locale) },
            ...(article.category
                ? [{ name: article.category.name, path: blogCategoryPath(locale, article.category.slug) }]
                : []),
            { name: article.title, path: blogArticlePath(locale, article.slug) },
        ];
        schemas.push(buildBreadcrumbSchema(breadcrumbItems));

        const faqItems = resolveBlogArticleFaqItems(article);
        if (faqItems.length >= 2) {
            schemas.push(buildFAQPageSchema(faqItems));
        }

        return schemas;
    };

    const fetchArticle = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get<Article>(buildApiUrl(`/blog/articles/${slug}`), {
                params: { site: 'pro', lang: locale },
            });
            setArticle(response.data);
        } catch (err: unknown) {
            console.error('Error fetching article:', err);
            if (axios.isAxiosError(err) && err.response?.status === 404) {
                setError(t('blogArticlePage.errors.notFound'));
            } else {
                setError(t('blogArticlePage.errors.fetchFailed'));
            }
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString(i18n.language, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    };

    const toggleFaq = (index: number) => {
        setOpenFaqIndex(openFaqIndex === index ? null : index);
    };

    const scrollToHeading = (headingId: string) => {
        if (!headingId) return;
        const element = document.getElementById(headingId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            window.history.replaceState(null, '', `#${headingId}`);
        }
    };

    const getCanonicalUrl = () => {
        const articleSlug = article?.slug || slug || '';
        return absoluteCanonicalUrl(blogArticlePath(locale, articleSlug));
    };

    const shareArticle = async (platform: string) => {
        if (!article?.slug) return;
        const canonicalUrl = getCanonicalUrl();
        const title = article.title || '';

        if (platform === 'native') {
            const shared = await shareNative({ title, url: canonicalUrl });
            if (shared) {
                trackBlogArticleShare({ platform: 'native', slug: article.slug, locale });
            }
            return;
        }

        if (!SOCIAL_PLATFORMS.has(platform as SocialSharePlatform)) return;
        openSharePopup(buildSocialShareUrl(platform as SocialSharePlatform, canonicalUrl, title));
        trackBlogArticleShare({
            platform: platform as SocialSharePlatform,
            slug: article.slug,
            locale,
        });
    };

    const copyArticleLink = async () => {
        if (!article?.slug) return;
        const canonicalUrl = getCanonicalUrl();
        const ok = await copyTextToClipboard(canonicalUrl);
        if (ok) {
            setCopyFailed(false);
            setLinkCopied(true);
            trackBlogArticleShare({ platform: 'copy', slug: article.slug, locale });
            window.setTimeout(() => setLinkCopied(false), 2000);
        } else {
            setLinkCopied(false);
            setCopyFailed(true);
            window.setTimeout(() => setCopyFailed(false), 2500);
        }
    };

    const renderTocNav = (className?: string) => {
        if (!article?.table_of_contents?.length) return null;
        return (
            <nav className={className || 'toc-list'} aria-label={t('blogArticlePage.toc.ariaLabel')}>
                {article.table_of_contents.map((item, index) => (
                    <a
                        key={index}
                        href={`#${item.slug}`}
                        className={`toc-link toc-level-${item.level}`}
                        onClick={(e) => {
                            e.preventDefault();
                            scrollToHeading(item.slug);
                        }}
                    >
                        {item.text}
                    </a>
                ))}
            </nav>
        );
    };

    if (loading) {
        return (
            <div className="article-page">
                <div className="article-loading">
                    <div className="loading-spinner"></div>
                    <p>{t('blogArticlePage.loading')}</p>
                </div>
            </div>
        );
    }

    const hasToc = Boolean(article?.table_of_contents && article.table_of_contents.length > 0);

    const articleAlternatePaths = article
        ? buildBlogArticleAlternatePaths(
              {
                  language: article.language || locale,
                  slug: article.slug,
                  market: article.market,
              },
              article.translations || []
          )
        : undefined;

    if (error || !article) {
        return (
            <>
                <SEOHead
                    title={t('blogArticlePage.seo.notFoundTitle')}
                    description={t('blogArticlePage.seo.notFoundDescription')}
                    noindex
                />
                <div className="article-page">
                    <div className="article-error">
                        <span className="error-icon">😢</span>
                        <h1>{error || t('blogArticlePage.errors.notFound')}</h1>
                        <p>{t('blogArticlePage.errors.description')}</p>
                        <Link to={blogBasePath(locale)} className="btn-back">
                            {t('blogArticlePage.backToBlog')}
                        </Link>
                    </div>
                </div>
            </>
        );
    }

    const articleFaqItems = resolveBlogArticleFaqItems(article);

    return (
        <>
            <SEOHead
                title={article.meta_title || article.title}
                description={clipMetaDescription(article.meta_description || article.excerpt || '')}
                canonicalPath={blogArticlePath(locale, article.slug)}
                alternatePaths={articleAlternatePaths}
                ogImage={article.featured_image_url || '/og-image.jpg'}
                ogType="article"
                schemaData={buildSeoSchemas()}
            >
                <meta property="article:published_time" content={article.published_at} />
                <meta property="article:modified_time" content={article.updated_at} />
                <meta property="article:author" content={article.author_name} />
            </SEOHead>
            <div className="article-page">
                <header className="blog-article-hero blog-article-hero--under-fixed-nav">
                    <div className="blog-article-header">
                        <nav className="blog-breadcrumb" aria-label={t('blogPage.breadcrumb.ariaLabel')}>
                            <Link to={locale === 'fr' ? '/' : `/${locale}`} className="breadcrumb-link">
                                {t('blogPage.breadcrumb.home')}
                            </Link>
                            <span>/</span>
                            <Link to={blogBasePath(locale)} className="breadcrumb-link">
                                {t('blogPage.breadcrumb.blog')}
                            </Link>
                            {article.category ? (
                                <>
                                    <span>/</span>
                                    <Link
                                        to={blogCategoryPath(locale, article.category.slug)}
                                        className="breadcrumb-link"
                                    >
                                        {article.category.name}
                                    </Link>
                                </>
                            ) : null}
                        </nav>

                        {article.category ? (
                            <Link
                                to={blogCategoryPath(locale, article.category.slug)}
                                className="blog-article-category"
                            >
                                {article.category.icon ? `${article.category.icon} ` : null}
                                {article.category.name}
                            </Link>
                        ) : null}

                        <h1 className="blog-article-title">{article.title}</h1>

                        {article.excerpt ? <p className="blog-article-lead">{clipMetaDescription(article.excerpt)}</p> : null}

                        <div className="blog-article-meta">
                            <span className="meta-item">
                                <User size={16} strokeWidth={2} aria-hidden />
                                {article.author_name}
                            </span>
                            <time className="meta-item meta-item--time" dateTime={article.published_at}>
                                <Calendar size={16} strokeWidth={2} aria-hidden />
                                {formatDate(article.published_at)}
                            </time>
                            <span className="meta-item">
                                <Clock size={16} strokeWidth={2} aria-hidden />
                                {t('blogArticlePage.readingTime', { minutes: article.reading_time_minutes })}
                            </span>
                        </div>

                        <div className="blog-share-mobile">
                            <p className="blog-share-label">{t('blogArticlePage.share.label')}</p>
                            <ArticleShareButtons
                                onShare={shareArticle}
                                onCopy={copyArticleLink}
                                copied={linkCopied}
                                copyFailed={copyFailed}
                                canNativeShare={canNativeShare}
                            />
                        </div>
                    </div>
                </header>

                {article.featured_image_url ? (
                    <div className="article-featured-image">
                        <img src={article.featured_image_url} alt={article.featured_image_alt || article.title} />
                    </div>
                ) : null}

                <div
                    className={
                        hasToc
                            ? 'blog-content-wrapper'
                            : 'blog-content-wrapper blog-content-wrapper--no-sidebar'
                    }
                >
                    {hasToc ? (
                        <aside className="blog-sidebar">
                            <div className="blog-toc">
                                <h3 className="toc-title">
                                    <List size={18} strokeWidth={2.25} aria-hidden />
                                    {t('blogArticlePage.toc.title')}
                                </h3>
                                {renderTocNav()}
                            </div>

                            <div className="blog-share">
                                <p className="blog-share-label">{t('blogArticlePage.share.label')}</p>
                                <ArticleShareButtons
                                    onShare={shareArticle}
                                    onCopy={copyArticleLink}
                                    copied={linkCopied}
                                    copyFailed={copyFailed}
                                    canNativeShare={canNativeShare}
                                />
                            </div>
                        </aside>
                    ) : null}

                    <main className="blog-article-main">
                        {hasToc ? (
                            <details className="blog-toc-mobile">
                                <summary className="blog-toc-mobile-summary">
                                    <List size={18} strokeWidth={2.25} aria-hidden />
                                    {t('blogArticlePage.toc.title')}
                                </summary>
                                {renderTocNav('toc-list toc-list--mobile')}
                            </details>
                        ) : null}

                        <article className="blog-content" lang={i18n.language}>
                            <div className="blog-markdown blog-markdown--prose">
                                <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    components={{
                                        h1: ({ node: _n, children, ...rest }: MarkdownHeadingProps<'h1'>) => {
                                            const id = slugifyBlogHeadingSource(reactNodeToPlainText(children));
                                            return (
                                                <h2 id={id || undefined} className="blog-md-h1-as-h2" {...rest}>
                                                    {children}
                                                </h2>
                                            );
                                        },
                                        h2: ({ node: _n, children, ...rest }: MarkdownHeadingProps<'h2'>) => {
                                            const id = slugifyBlogHeadingSource(reactNodeToPlainText(children));
                                            return (
                                                <h2 id={id || undefined} {...rest}>
                                                    {children}
                                                </h2>
                                            );
                                        },
                                        h3: ({ node: _n, children, ...rest }: MarkdownHeadingProps<'h3'>) => {
                                            const id = slugifyBlogHeadingSource(reactNodeToPlainText(children));
                                            return (
                                                <h3 id={id || undefined} {...rest}>
                                                    {children}
                                                </h3>
                                            );
                                        },
                                        hr: () => <hr className="blog-md-hr" />,
                                        table: ({ children, node: _node, ...tableProps }) => (
                                            <div className="blog-md-table-wrap">
                                                <table {...tableProps}>{children}</table>
                                            </div>
                                        ),
                                    }}
                                >
                                    {article.content}
                                </ReactMarkdown>
                            </div>
                        </article>

                        {article.tags && article.tags.length > 0 ? (
                            <div className="article-tags">
                                <span className="tags-label">
                                    <Tag size={16} strokeWidth={2} aria-hidden />
                                    {t('blogArticlePage.tags.label')}
                                </span>
                                {article.tags.map((tag, index) => (
                                    <Link
                                        key={index}
                                        to={`${blogBasePath(locale)}?tag=${encodeURIComponent(tag)}`}
                                        className="tag"
                                    >
                                        {tag}
                                    </Link>
                                ))}
                            </div>
                        ) : null}

                        {articleFaqItems.length > 0 ? (
                            <section className="blog-faq-section" aria-labelledby="blog-faq-heading">
                                <h2 id="blog-faq-heading" className="faq-title">
                                    <HelpCircle size={22} strokeWidth={2.25} aria-hidden />
                                    {t('blogArticlePage.faq.title')}
                                </h2>
                                <div className="article-faq-list">
                                    {articleFaqItems.map((faq, index) => (
                                        <div
                                            key={index}
                                            className={`article-faq-item ${openFaqIndex === index ? 'active' : ''}`}
                                        >
                                            <button
                                                type="button"
                                                className="article-faq-question"
                                                onClick={() => toggleFaq(index)}
                                                aria-expanded={openFaqIndex === index}
                                            >
                                                <span>{faq.question}</span>
                                                <span className="article-faq-icon">
                                                    {openFaqIndex === index ? '−' : '+'}
                                                </span>
                                            </button>
                                            <div className="article-faq-answer">
                                                <p>{faq.answer}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        ) : null}

                        {article.related_articles && article.related_articles.length > 0 ? (
                            <section className="article-related" aria-labelledby="blog-related-heading">
                                <h2 id="blog-related-heading" className="article-related-title">
                                    <BookOpen size={22} strokeWidth={2.25} aria-hidden />
                                    {t('blogArticlePage.related.title')}
                                </h2>
                                <div className="related-grid">
                                    {article.related_articles.map((related) => (
                                        <Link
                                            key={related.id}
                                            to={blogArticlePath(locale, related.slug)}
                                            className="related-card"
                                            onClick={() => trackRelated(related.slug)}
                                        >
                                            {related.featured_image_url ? (
                                                <img
                                                    src={related.featured_image_url}
                                                    alt={related.title}
                                                    className="related-image"
                                                />
                                            ) : null}
                                            <div className="related-content">
                                                <h3>{related.title}</h3>
                                                {related.excerpt ? (
                                                    <p className="related-excerpt">{clipMetaDescription(related.excerpt)}</p>
                                                ) : null}
                                                <div className="related-meta">
                                                    <time dateTime={related.published_at}>
                                                        {formatDate(related.published_at)}
                                                    </time>
                                                    {related.reading_time_minutes ? (
                                                        <span>{t('blogPage.article.readingTime', { minutes: related.reading_time_minutes })}</span>
                                                    ) : null}
                                                </div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </section>
                        ) : null}

                        <section className="blog-cta-section" aria-labelledby="blog-cta-title">
                            <h2 id="blog-cta-title" className="blog-cta-title">
                                {t('blogArticlePage.cta.title')}
                            </h2>
                            <p className="blog-cta-lede">
                                {t('blogArticlePage.cta.lede')}
                            </p>
                            <MarketingCTA
                                label={t('blogArticlePage.cta.button')}
                                trackEvent="blog_article_cta"
                                onTrack={() => trackCta('signup', '/signup-user')}
                            />
                        </section>

                        <div className="article-back">
                            <Link to={blogBasePath(locale)} className="btn-back">
                                {t('blogArticlePage.backToBlog')}
                            </Link>
                        </div>
                    </main>
                </div>
            </div>
        </>
    );
};

export default BlogArticlePage;
